import { useAccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import {
  NOTIFICATION_TYPE_ACCESS_REQUEST_APPROVED,
  NOTIFICATION_TYPE_ACCESS_REQUEST_DENIED,
  NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED,
} from '../../../constants';
import { formatAccessRequestHeader } from '../../../utils';
import type { NotificationGroupInfo } from '../types';
import { useHeaderFragment } from './gql/generated/Header';

const MAX_USER_NAME_LENGTH = 35;

export const useHeaderData = ({
  id,
  notificationType,
}: NotificationGroupInfo) => {
  const isAccessRequestSubmission =
    notificationType === NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED;
  const isAccessRequestApprovedOrDenied =
    notificationType === NOTIFICATION_TYPE_ACCESS_REQUEST_APPROVED ||
    notificationType === NOTIFICATION_TYPE_ACCESS_REQUEST_DENIED;

  const { complete: completeNotificationHeader, data: notificationHeaderData } =
    useHeaderFragment({
      from: {
        id,
        __typename: notificationType,
        skip: isAccessRequestSubmission,
      },
    });

  const { complete: completeAccessRequestNode, data: accessRequestNodeData } =
    useAccessRequestNodeFragment({
      from: {
        id,
        __typename: 'AccessRequest',
        skip: !isAccessRequestSubmission,
      },
    });

  const complete = completeNotificationHeader || completeAccessRequestNode;

  let data;
  if (isAccessRequestSubmission) {
    data = formatAccessRequestHeader(accessRequestNodeData);
  } else {
    data = notificationHeaderData;
  }

  const headerContent =
    'content' in data ? data?.content?.layout?.header : null;

  if (
    !complete ||
    (!isAccessRequestSubmission && !headerContent) ||
    (isAccessRequestSubmission && !accessRequestNodeData)
  )
    return null;

  let text = '';
  let assetName: string | null = null;
  let shouldTruncateUserName = false;
  let firstItem = null;

  if (!isAccessRequestSubmission) {
    const layoutItemUserName = headerContent?.find(
      (item) => item?.__typename === 'NotificationLayoutItemUserName'
    );
    const userName =
      layoutItemUserName && 'content' in layoutItemUserName
        ? layoutItemUserName.content
        : null;

    const layoutItemAssetName = headerContent?.find(
      (item) => item?.__typename === 'NotificationLayoutItemAssetName'
    );
    assetName =
      layoutItemAssetName &&
      'content' in layoutItemAssetName &&
      typeof layoutItemAssetName.content === 'string'
        ? layoutItemAssetName.content
        : null;

    firstItem =
      headerContent?.[0] && 'content' in headerContent?.[0]
        ? headerContent?.[0]
        : null;
    const isFirstItemUserName =
      firstItem?.__typename === 'NotificationLayoutItemUserName';

    shouldTruncateUserName =
      !isAccessRequestApprovedOrDenied &&
      Boolean(
        isFirstItemUserName &&
          userName &&
          userName.length > MAX_USER_NAME_LENGTH
      );

    const content = shouldTruncateUserName
      ? headerContent?.slice(1)
      : headerContent;

    if (!content) return null;

    text = content
      .map((item) => (item && 'content' in item ? item.content : null))
      .filter((itemContent) => itemContent)
      .join(' ');
  } else {
    const accessRequestData = isAccessRequestSubmission ? data : null;
    if (!accessRequestData) return null;

    text =
      'text' in accessRequestData ? (accessRequestData.text as string) : '';
    shouldTruncateUserName = false;
  }

  return {
    text,
    firstItem,
    assetName,
    shouldTruncateUserName,
  };
};
